import { Stack, Typography } from "@mui/material";
import { CommentInput } from "./CommentInput";
import { CommentThread } from "./CommentThread";
import { CommentThreadFragment } from "generated-graphql/graphql";

interface GenericCommentStreamProps {
  title?: string;
  comments: CommentThreadFragment[];
  currentUserId?: string;
  onAddComment: (message: string) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
  addLoading?: boolean;
  deleteLoading?: boolean;
  emptyStateMessage?: string;
  placeholder?: string;
  buttonText?: string;
  showDeleteButton?: boolean;
  disabled?: boolean;
}

/**
 * Generic comment stream component, any entity that has a comment with @Prisma.Comment can use this component
 *
 * @example
 * ```tsx
 * const { addComment, loading: addLoading } = useAddMyEntityComment({ entityId });
 * const { deleteComment, loading: deleteLoading } = useDeleteMyEntityComment({ commentId });
 *
 * <GenericCommentStream
 *   title="Entity Comments"
 *   comments={entity.comments}
 *   currentUserId={currentUser.id}
 *   onAddComment={addComment}
 *   onDeleteComment={deleteComment}
 *   addLoading={addLoading}
 *   deleteLoading={deleteLoading}
 *   placeholder="Add a comment to this entity..."
 *   buttonText="Comment"
 * />
 * ```
 */
export function GenericCommentStream({
  title = "Comments",
  comments,
  currentUserId,
  onAddComment,
  onDeleteComment,
  addLoading = false,
  deleteLoading = false,
  emptyStateMessage = "No comments yet.",
  placeholder = "Add a comment...",
  buttonText = "Add Comment",
  showDeleteButton = true,
  disabled = false,
}: GenericCommentStreamProps) {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>

      {/* Display existing comments section */}
      <CommentThread
        comments={comments}
        currentUserId={currentUserId}
        onDeleteComment={onDeleteComment}
        deleteLoading={deleteLoading}
        emptyStateMessage={emptyStateMessage}
        showDeleteButton={showDeleteButton}
      />

      {/* Add new comment section */}
      <CommentInput
        onSubmit={onAddComment}
        loading={addLoading}
        placeholder={placeholder}
        buttonText={buttonText}
        rows={1}
        disabled={disabled}
      />
    </Stack>
  );
}
